import { TestCaseSearchResult } from "../../../../../../api/models/test-series/TestCaseSearchResult";
import { RowData } from "../interfaces/RowData";
import { FilterableColumn } from "../interfaces/FilterableColumn";

const PASSED = "Passed";
const NOT_PASSED = "Not passed";
const EMPTY = "Empty";
const SOME_DATA = "Some data";
const FULL = "Full";

export function getFilterOptions(
  testCases: TestCaseSearchResult[],
  testSeriesObjectIndexNumber: number,
): Record<FilterableColumn, string[]> {
  return {
    id: testCases.map((testCase) => getTestCaseRowId(testSeriesObjectIndexNumber, testCase.indexNumber)),
    firstSubstrate: distinctNotNull(testCases.map((testCase) => testCase.firstSubstrateActionFlowName)),
    secondSubstrate: distinctNotNull(testCases.map((testCase) => testCase.secondSubstrateActionFlowName)),
    product: distinctNotNull(testCases.map((testCase) => testCase.productSampleActionFlowName)),
    buildingAction: distinctNotNull(testCases.map((testCase) => testCase.buildingActionFlowName)),
    condition: distinctNotNull(testCases.map((testCase) => testCase.conditionName)),
    treatment: distinctNotNull(testCases.map((testCase) => testCase.treatmentActionFlowName)),
    passed: [PASSED, NOT_PASSED],
    progress: [EMPTY, SOME_DATA, FULL],
  };
}

export function filterRows(
  rows: RowData[],
  filter: Record<FilterableColumn, string[]>,
  testSeriesObjectIndexNumber: number,
): RowData[] {
  let filteredRows = rows;
  if (filter.id.length > 0) {
    filteredRows = filterByAttribute(
      filteredRows,
      (rowData) => getTestCaseRowId(testSeriesObjectIndexNumber, rowData.indexNumber),
      filter.id,
    );
  }
  if (filter.firstSubstrate.length > 0) {
    filteredRows = filterByAttribute(filteredRows, (rowData) => rowData.firstSubstrate, filter.firstSubstrate);
  }
  if (filter.secondSubstrate.length > 0) {
    filteredRows = filterByAttribute(filteredRows, (rowData) => rowData.secondSubstrate, filter.secondSubstrate);
  }
  if (filter.product.length > 0) {
    filteredRows = filterByAttribute(filteredRows, (rowData) => rowData.product, filter.product);
  }
  if (filter.buildingAction.length > 0) {
    filteredRows = filterByAttribute(filteredRows, (rowData) => rowData.buildingAction, filter.buildingAction);
  }
  if (filter.condition.length > 0) {
    filteredRows = filterByAttribute(filteredRows, (rowData) => rowData.condition, filter.condition);
  }
  if (filter.treatment.length > 0) {
    filteredRows = filterByAttribute(filteredRows, (rowData) => rowData.treatment, filter.treatment);
  }
  if (filter.passed.length > 0) {
    filteredRows = filterByPassed(filteredRows, filter.passed);
  }
  if (filter.progress.length > 0) {
    filteredRows = filterByProgress(filteredRows, filter.progress);
  }

  return filteredRows;
}

function filterByAttribute(rows: RowData[], getAttribute: (rowData: RowData) => string | undefined, filter: string[]) {
  return rows.filter((rowData) => {
    const attribute = getAttribute(rowData);
    return attribute && filter.includes(attribute);
  });
}

function filterByPassed(rows: RowData[], passedFilter: string[]): RowData[] {
  return rows.filter(
    (rowData) =>
      (passedFilter.includes(PASSED) && rowData.rating === "PASSED") ||
      (passedFilter.includes(NOT_PASSED) && rowData.rating !== "PASSED"),
  );
}

function filterByProgress(rows: RowData[], progressFilter: string[]): RowData[] {
  return rows.filter(
    (rowData) =>
      (progressFilter.includes(EMPTY) && rowData.progress.completed === 0) ||
      (progressFilter.includes(SOME_DATA) &&
        rowData.progress.completed > 0 &&
        rowData.progress.completed < rowData.progress.total) ||
      (progressFilter.includes(FULL) && rowData.progress.completed === rowData.progress.total),
  );
}

function distinctNotNull(array: string[]): string[] {
  return array.filter((value, index, self) => {
    return value != null && self.indexOf(value) === index;
  });
}

function getTestCaseRowId(testSeriesObjectIndexNumber: number, testCaseIndexNumber: number) {
  return `${testSeriesObjectIndexNumber}-${testCaseIndexNumber}`;
}
